import React from 'react'
import { Chip, Tooltip, Typography } from '@mui/material'

import { campaignAttribution } from '../../utils/codingBatches'

/**
 * A batch's campaign, as the reader of a spend column needs it.
 *
 * This is the one fact about a batch that decides whose money paid for it, and
 * until now it was readable only in psql: the label was stored, rode onto
 * `llm_request` and `token_usage_logs`, and drove the entire spend report,
 * while every batch surface in the product showed tokens with no way to tell
 * which product they belonged to.
 *
 * THE THREE STATES ARE DRAWN DIFFERENTLY ON PURPOSE (see campaignAttribution):
 * a real campaign is a plain value, `unknown` is a WARNING — since C4 the
 * Sprinklr sender refuses to make one, so seeing it means a pre-C4 batch or a
 * sender that stopped honouring the rule — and no label at all is settled
 * history, drawn as disabled text so it reads as "before labels existed"
 * rather than as a failure.
 *
 * Shared by the pipeline's batches table and the job-wide one so the two can
 * never drift into telling different stories about the same batch.
 */
export interface CampaignLabelProps {
  labels?: Record<string, string> | null
  /** Optional resolved display name; the id is always shown as the tooltip. */
  name?: string
}

const CampaignLabel: React.FC<CampaignLabelProps> = ({ labels, name }) => {
  const attribution = campaignAttribution(labels)

  if (attribution.kind === 'unlabelled') {
    return (
      <Tooltip title="This batch was created before batches carried labels. Its cost is real but attributable to no product — the spend report shows it as unattributed.">
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      </Tooltip>
    )
  }

  if (attribution.kind === 'unknown') {
    return (
      <Tooltip title="This batch's campaign could not be determined, so its tokens are billed to no product. The Sprinklr sender no longer creates these — it holds unattributable records back instead — so this is either an older batch or a sender that stopped honouring that rule.">
        <Chip size="small" color="warning" variant="outlined" label="unknown" />
      </Tooltip>
    )
  }

  return (
    <Tooltip title={attribution.value}>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
        {name || attribution.value}
      </Typography>
    </Tooltip>
  )
}

export default CampaignLabel
